// My training on the phone (18 §18.1, §18.6): the walkthroughs of the screens this person can open,
// with what is new, done, changed or skipped. Each row opens its screen and starts the walkthrough.
import { useMemo } from 'react';
import { trainingPlan, type WalkthroughStatus } from '@ecobox/domain';
import { Badge, Button, type BadgeTone } from '@ecobox/ui';
import { useOpsHelp } from '../help/HelpProvider';

const STATUS: Record<WalkthroughStatus, { label: string; tone: BadgeTone }> = {
  new: { label: 'New', tone: 'info' },
  done: { label: 'Done', tone: 'success' },
  updated: { label: 'Changed', tone: 'warning' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

export function TrainingPage() {
  const { entries, records, open } = useOpsHelp();
  const plan = useMemo(
    () =>
      trainingPlan(
        entries.map((e) => e.walkthrough),
        records ?? {},
      ),
    [entries, records],
  );
  const status = new Map(plan.items.map((i) => [i.walkthrough.id, i.status]));
  return (
    <section className="ops-training">
      <h1 className="ops-title">My training</h1>
      <p data-tour="training-progress">
        {records === undefined
          ? 'Loading your training…'
          : `${plan.done} of ${plan.total} done (${plan.percent}%)`}
      </p>
      <ul className="ops-training__list" aria-label="Walkthroughs" data-tour="training-list">
        {entries.map((e) => {
          const s = STATUS[status.get(e.walkthrough.id) ?? 'new'];
          const fixed = 'path' in e.open;
          return (
            <li key={e.walkthrough.id} data-walkthrough={e.walkthrough.id}>
              <span className="ops-training__what">
                <strong>{e.walkthrough.pageTitle}</strong> <Badge tone={s.tone}>{s.label}</Badge>
                <span className="ops-training__hint">
                  {e.walkthrough.summary}
                  {'from' in e.open ? ` Opens from ${e.open.label}.` : ''}
                </span>
              </span>
              <Button
                size="touch"
                variant={s.label === 'Done' ? 'secondary' : 'primary'}
                aria-label={`Take it: ${e.walkthrough.pageTitle}`}
                onClick={() => open(e)}
              >
                {fixed
                  ? s.label === 'Done'
                    ? 'Again'
                    : 'Take it'
                  : `Open ${'from' in e.open ? e.open.label : ''}`}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
